'use client';

import { useBunja } from 'bunja/react';
import { formatISO } from 'date-fns';
import { useAtomValue } from 'jotai';
import { useEffect, useMemo, useState } from 'react';

import { UserKycStatus } from '@flipster/apis/v1/kyc/types';
import { Button } from '@flipster/react/design-system';
import CautionCircleIcon from '@flipster/react/icon/generated/CautionCircleIcon';
import UserCautionColorIcon from '@flipster/react/icon/generated/UserCautionColorIcon';
import { css } from '@flipster/styled-system/css';
import { flex } from '@flipster/styled-system/patterns';
import { Modal } from '@flipster/ui';
import { formatUTCTimeZone } from '@flipster/utils/datetime';
import { setLocalStorage } from '@flipster/utils/localStorage';
import segment, { KycEvent } from '@flipster/utils/segment';

import { KYC_MODAL_HIDE_TIMESTAMP_KEY, kycPopupBunja } from './bunja';

const getGracePeriodStatusAndComment = (kycInfo?: UserKycStatus) => {
  if (!kycInfo) {
    return { status: null, comment: null, level: null };
  }

  const { gracePeriod, level2Status, level3Status, level4Status, level2Comment, level3Comment, level4Comment } =
    kycInfo;

  // gracePeriod가 null일 경우 처리
  if (!gracePeriod) {
    return { status: null, comment: null, level: null };
  }

  // gracePeriod에 해당하는 레벨 이름과 상태를 확인
  const { levelName } = gracePeriod;
  let status = null;
  let comment = null;

  // gracePeriod에 해당하는 레벨 상태와 코멘트 추출
  switch (levelName) {
    case 'LEVEL_2':
      status = level2Status;
      comment = level2Comment;
      break;
    case 'LEVEL_3':
      status = level3Status;
      comment = level3Comment;
      break;
    case 'LEVEL_4':
      status = level4Status;
      comment = level4Comment;
      break;
    default:
      return { status: null, comment: null, level: null }; // 정의되지 않은 레벨의 경우
  }

  // 상태가 RESUBMIT 또는 REJECTED인 경우 상태와 코멘트를 반환
  if (status === 'RESUBMIT' || status === 'REJECTED') {
    const formattedStatus = status === 'REJECTED' ? 'Rejected' : 'Resubmit Required';
    const formattedLevel = `Level ${levelName.split('_')[1]}`;

    return { status: formattedStatus, comment, level: formattedLevel };
  }

  // 해당 조건에 맞는 상태가 없으면 null 반환
  return { status: null, comment: null, level: null };
};

interface Props {
  onClose: VoidFunction;
}

const KycPopup = ({ onClose }: Props) => {
  const { kycInfoAtom, isShowKycPopupAtom } = useBunja(kycPopupBunja);
  const kycInfo = useAtomValue(kycInfoAtom);
  const initialShowState = useAtomValue(isShowKycPopupAtom);
  const [isShow, setIsShow] = useState(initialShowState);

  const { status, comment, level } = useMemo(() => getGracePeriodStatusAndComment(kycInfo), [kycInfo]);

  const handleCloseModal = () => {
    setLocalStorage(KYC_MODAL_HIDE_TIMESTAMP_KEY, formatISO(new Date()));
    setIsShow(false);
    onClose();
  };

  const handleClickVerifyNow = () => {
    handleCloseModal();
    segment.track(KycEvent.KycPopUpClick, { kyc_review_type: status });
  };

  useEffect(() => {
    if (kycInfo) {
      segment.track(KycEvent.KycPopUpView, { kyc_review_type: status });
    }
  }, [kycInfo, status]);

  return (
    <Modal
      isShow={isShow}
      size='large'
      onClose={handleCloseModal}
      header={
        <div className={flex({ align: 'center', gap: 8, tablet: { gap: 12 } })}>
          <UserCautionColorIcon className={css({ w: 24, h: 24, tablet: { w: 32, h: 32 } })} />
          <span className={css({ textStyle: 'Title/M' })}>KYC required</span>
        </div>
      }
      withFooter
    >
      <p className={css({ textStyle: 'Text/L/Regular', mb: 24 })}>
        <span className={css({ tablet: { display: 'block' } })}>
          Your document needs resubmission due to the reason below.{' '}
        </span>
        <span className={css({ color: 'content.default.level-5' })}>
          Provide updated documents required below by{' '}
          {formatUTCTimeZone(kycInfo?.gracePeriod?.finishAt, 'yy.MM.dd HH:mm')} UTC to maintain your verification
          status.
        </span>
      </p>
      <div className={css({ p: 16, bg: 'background.default.level-2', rounded: 4 })}>
        <div className={flex({ align: 'center', justify: 'space-between', mb: 8 })}>
          <span className={flex({ align: 'center', gap: 4, color: 'content.state.negative' })}>
            <CautionCircleIcon size={16} />
            <span className={css({ textStyle: 'Text/L/Strong' })}>{status}</span>
          </span>
          <span className={css({ textStyle: 'Text/L/Strong' })}>{level}</span>
        </div>
        <p className={css({ textStyle: 'Text/M/Regular', whiteSpace: 'pre-line' })}>{comment}</p>
      </div>
      <footer>
        <Button
          as='a'
          size='large'
          label='Verify Now'
          href='/user/kyc'
          onClick={handleClickVerifyNow}
          css={{ flex: 1, tablet: { flex: 'unset', w: 207 } }}
        />
      </footer>
    </Modal>
  );
};

export default KycPopup;
